/**
 * Module-level store for active group SSE streams.
 *
 * When ActiveRunPanel unmounts (e.g. navigating to content-upload), the SSE
 * connection is kept alive in this registry so it can be reconnected on remount
 * without losing all per-device streaming state.
 *
 * Streams persist until the group run ends naturally or the browser tab closes.
 */
import { getAuthToken } from './api';
import { getGroupStreamUrl, getGroupState } from './vmos-agentic-group-api';
import { parseGroupEvent } from '@/components/vmos/utils';
import type { AgentEvent, DeviceStreamState } from '@/components/vmos/shared-types';

export type StreamStateListener = (padId: string, state: Partial<DeviceStreamState>) => void;

interface StreamSubscriber {
  listener: StreamStateListener;
  onStreamEnd: () => void;
}

interface StreamEntry {
  reader: ReadableStreamDefaultReader<Uint8Array> | null;
  controller: AbortController;
  subscribers: Set<StreamSubscriber>;
  deviceStates: Record<string, DeviceStreamState>;
  promise: Promise<void> | null;
}

const streams = new Map<string, StreamEntry>();

export function subscribeToGroupStream(
  groupId: string,
  padIds: string[],
  listener: StreamStateListener,
  onStreamEnd: () => void,
) {
  const subscriber: StreamSubscriber = { listener, onStreamEnd };

  // If stream already exists for this group, rejoin it
  const existing = streams.get(groupId);
  if (existing) {
    existing.subscribers.add(subscriber);
    for (const [padId, state] of Object.entries(existing.deviceStates)) {
      listener(padId, state);
    }
    return () => {
      existing.subscribers.delete(subscriber);
    };
  }

  // Create a new stream — seed deviceStates from the API snapshot first,
  // then start the SSE stream for live updates.
  const controller = new AbortController();
  const initialStates: Record<string, DeviceStreamState> = {};
  for (const padId of padIds) {
    initialStates[padId] = { status: 'running', step: 0, maxSteps: 200, messages: [] };
  }

  const entry: StreamEntry = {
    reader: null,
    controller,
    subscribers: new Set([subscriber]),
    deviceStates: initialStates,
    promise: null,
  };
  streams.set(groupId, entry);

  // Seed from the backend state snapshot first, then start the SSE stream.
  // This runs in the background so the component renders immediately with
  // defaults, then gets real values once the snapshot arrives.
  entry.promise = (async () => {
    try {
      const snapshot = await getGroupState(groupId);
      if (snapshot?.devices) {
        for (const [padId, dev] of Object.entries(snapshot.devices)) {
          const status = dev.status === 'running'
            ? 'running' as const
            : dev.status === 'completed'
              ? 'completed' as const
              : dev.status === 'failed'
                ? 'failed' as const
                : dev.status === 'stopped'
                  ? 'stopped' as const
                  : 'running' as const;
          const state: DeviceStreamState = {
            status,
            step: dev.step ?? 0,
            maxSteps: dev.max_steps ?? 200,
            messages: entry.deviceStates[padId]?.messages || [],
          };
          entry.deviceStates[padId] = state;
          for (const s of entry.subscribers) {
            s.listener(padId, state);
          }
        }
      }
    } catch {
      // Silently fall through — the SSE stream will populate states over time
    }
    await runStream(groupId, entry);
  })();

  return () => {
    entry.subscribers.delete(subscriber);
  };
}

function cleanupStream(groupId: string) {
  const entry = streams.get(groupId);
  if (!entry) return;
  entry.controller.abort();
  streams.delete(groupId);
}

function notifyStreamEnd(entry: StreamEntry) {
  // Every current subscriber (including components that rejoined after the
  // stream was created) must learn the run ended.
  const subscribers = Array.from(entry.subscribers);
  entry.subscribers.clear();
  for (const s of subscribers) {
    try {
      s.onStreamEnd();
    } catch {
      /* subscriber callback errors shouldn't break teardown */
    }
  }
}

async function runStream(groupId: string, entry: StreamEntry) {
  const MAX_RETRIES = 5;
  let retries = 0;

  while (retries <= MAX_RETRIES && !entry.controller.signal.aborted) {
    try {
      const token = await getAuthToken();
      const url = getGroupStreamUrl(groupId);
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        signal: entry.controller.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error('Failed to connect to group stream');
      }

      retries = 0;
      entry.reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await entry.reader.read();
        if (done) break;
        if (entry.controller.signal.aborted) return;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          if (entry.controller.signal.aborted) return;

          try {
            const event: AgentEvent = JSON.parse(line.slice(6));
            handleStreamEvent(event, entry);
          } catch {
            // skip malformed JSON
          }
        }
      }

      notifyStreamEnd(entry);
      cleanupStream(groupId);
      return;
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      if (entry.controller.signal.aborted) return;

      retries++;
      if (retries <= MAX_RETRIES) {
        const delay = Math.min(1000 * Math.pow(2, retries - 1), 10000);
        await new Promise((r) => setTimeout(r, delay));
        continue;
      }
      notifyStreamEnd(entry);
      cleanupStream(groupId);
      return;
    }
  }
}

function handleStreamEvent(event: AgentEvent, entry: StreamEntry) {
  if (!event.profile_id) return;
  const padId = event.profile_id;

  let stateUpdate: Partial<DeviceStreamState> | null = null;

  if (event.event === 'progress' && event.data) {
    const s = event.data.status;
    const status = s === 'completed' || s === 'failed' || s === 'stopped' ? s : 'running' as const;
    stateUpdate = {
      status,
      step: event.data.step ?? (entry.deviceStates[padId]?.step ?? 0),
      maxSteps: event.data.max_steps ?? (entry.deviceStates[padId]?.maxSteps ?? 200),
    };
  }

  if (event.event === 'result' && event.data) {
    const terminalStatus = event.data.status === 'completed'
      ? 'completed' as const
      : event.data.status === 'stopped'
        ? 'stopped' as const
        : 'failed' as const;
    stateUpdate = { status: terminalStatus };
  }

  if (event.event === 'error' && event.data && !event.data.recoverable) {
    stateUpdate = { status: 'failed' as const };
  }

  // Parse for message content
  const parsed = parseGroupEvent(event);
  if (parsed) {
    if (!stateUpdate) stateUpdate = {};
    const current = entry.deviceStates[parsed.padId];
    stateUpdate.messages = [...(current?.messages || []), parsed.message].slice(-100);
  }

  if (stateUpdate) {
    const current = entry.deviceStates[padId] || { status: 'running' as const, step: 0, maxSteps: 200, messages: [] };
    entry.deviceStates[padId] = { ...current, ...stateUpdate };
    for (const s of entry.subscribers) {
      s.listener(padId, stateUpdate);
    }
  }
}

export function getCachedDeviceStates(groupId: string): Record<string, DeviceStreamState> | null {
  const entry = streams.get(groupId);
  return entry ? { ...entry.deviceStates } : null;
}

export function hasActiveStream(groupId: string): boolean {
  return streams.has(groupId);
}
